import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getMarketplaceDeployOrigin } from '../../api/resolveApiBaseUrl';

const INITIAL_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;

function buildSellerNotificationsWsUrl() {
  const explicit = process.env.REACT_APP_SELLER_NOTIFY_WS_URL;
  if (explicit) {
    return explicit.replace(/\/?$/, '');
  }
  const httpBase =
    process.env.REACT_APP_API_BASE_URL ||
    getMarketplaceDeployOrigin() ||
    'http://localhost:8001';

  try {
    const u = new URL(httpBase);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${u.origin}/ws/seller-notifications/`;
  } catch {
    return '';
  }
}

/**
 * Exponential backoff with full jitter so recovering origins aren't hit by a
 * synchronized reconnect stampede (thundering herd).
 * attempt 0 → ~0–1s, 1 → ~0–2s, 2 → ~0–4s, … capped at MAX_RETRY_MS.
 */
function nextReconnectDelayMs(attempt) {
  const exp = Math.min(MAX_RETRY_MS, INITIAL_RETRY_MS * 2 ** Math.max(0, attempt));
  return Math.floor(Math.random() * (exp + 1));
}

/**
 * Opens WebSocket while authenticated (cookie carries JWT).
 * Shows congratulations toast when someone buys seller inventory — no buyer identity.
 * Reconnects with exponential backoff + jitter after drops.
 */
export default function SellerSaleNotificationsBridge() {
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const attemptRef = useRef(0);
  const dedupeRef = useRef({ key: '', at: 0 });

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') {
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_e) {
          /* noop */
        }
        wsRef.current = null;
      }
      return undefined;
    }

    const url = buildSellerNotificationsWsUrl();
    if (!url) {
      return undefined;
    }

    let cancelled = false;

    const showDedupedToast = (title, message) => {
      const key = `${title}|${message}`;
      const now = Date.now();
      if (key === dedupeRef.current.key && now - dedupeRef.current.at < 8000) {
        return;
      }
      dedupeRef.current = { key, at: now };
      toast.success(
        () => (
          <div>
            <strong>{title}</strong>
            <div style={{ marginTop: '8px', fontWeight: 500 }}>{message}</div>
          </div>
        ),
        { autoClose: 12000 },
      );
    };

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      clearReconnectTimer();
      const delay = nextReconnectDelayMs(attemptRef.current);
      attemptRef.current += 1;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        if (!cancelled) connect();
      }, delay);
    };

    function closeSocketQuietly() {
      const existing = wsRef.current;
      if (!existing) return;
      wsRef.current = null;
      try {
        existing.onopen = null;
        existing.onmessage = null;
        existing.onerror = null;
        existing.onclose = null;
        existing.close();
      } catch (_e) {
        /* noop */
      }
    }

    function connect() {
      if (cancelled) return;

      // Already open or handshake in flight — don't open a second socket.
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        return;
      }

      clearReconnectTimer();
      closeSocketQuietly();

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          attemptRef.current = 0;
        };

        ws.onmessage = (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data?.type === 'seller_order_completed' && data.message) {
              showDedupedToast(data.title || 'Congratulations!', data.message);
            }
          } catch (_e) {
            /* ignore malformed */
          }
        };

        ws.onclose = () => {
          if (wsRef.current === ws) {
            wsRef.current = null;
          }
          if (cancelled) return;
          scheduleReconnect();
        };

        ws.onerror = () => {
          // onclose will schedule reconnect; avoid double-scheduling here.
          try {
            ws.close();
          } catch (_e) {
            /* noop */
          }
        };
      } catch (_e) {
        scheduleReconnect();
      }
    }

    // Resume faster when the network or tab comes back (still uses backoff if it fails).
    const onOnline = () => {
      if (cancelled) return;
      const state = wsRef.current?.readyState;
      if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
        clearReconnectTimer();
        connect();
      }
    };

    const onVisibility = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const state = wsRef.current?.readyState;
      if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
        clearReconnectTimer();
        connect();
      }
    };

    connect();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearReconnectTimer();
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      closeSocketQuietly();
    };
  }, [isAuthenticated]);

  return null;
}
